import { Component, HostBinding, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription, map, debounceTime, distinctUntilChanged } from 'rxjs';
import {
	FieldExpression,
	LuceneExpression,
	NodeExpression,
	term,
	parse as luceneToParse,
	toString as luceneToString
} from 'lucene';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { WEIGHT_OPTIONS, PRODUCT_OPTIONS } from '../../../shared/models/list';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { UNDERSCORE } from '../../../shared/components/utils/constants';
import { OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { ARTICLE, GROSS, CDD } from '../../../shared/models/settings';
import { Batch } from '../../models/batch';
import { BatchesService } from '../../services/batches.service';
import { ListFilterService } from '../../../shared/services/list-filter.service';
import { ListFilter } from '../../../shared/models/filter';
import { DatacenterType } from '../../../shared/models/datacenter-aggrid-view';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';

@Component({
	selector: 'copo-batch-list',
	templateUrl: './batch-list.component.html',
	styleUrl: './batch-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		VirtualScrollerDirective,
		NgFor,
		RouterLink,
		LoadingSpinnerComponent,
		NumberPipe,
		DimensionPipe
	]
})
export class BatchListComponent implements OnInit, OnDestroy {
	@HostBinding('class.copo-order-tracking-block') readonly block = true;

	readonly UNDERSCORE = UNDERSCORE;

	user: User;
	productDisplay: keyof typeof PRODUCT_OPTIONS = ARTICLE;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	sortWay = OrderByDirection.DESC;
	sortBy: keyof Batch = CDD;
	requiredFields: (keyof Batch)[] = [
		'id',
		'supplierOrderNumber',
		'supplierOrderItemNumber',
		'customerOrderNumber',
		'customerOrderItemNumber',
		'thickness',
		'width',
		'length',
		'cddWeek',
		'batchReference',
		'daysInStock',
		'statusDescription',
		'millDescription'
	];

	searchTextControl = new FormControl('', { nonNullable: true });
	searchTextSubscription?: Subscription;
	filters = '';
	mergedFilter?: LuceneExpression;
	currentFilters?: ListFilter;
	datacenterType: DatacenterType;
	constructor(
		private readonly batchService: BatchesService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly listFilterService: ListFilterService
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.data['type'];
		this.currentFilters = this.getAppliedFilters();
		let criterionFilterNode;
		if (this.listFilterService
			.getLuceneFromAllFilters(this.currentFilters)) {
			criterionFilterNode = this.listFilterService.getLuceneFromAllFilters(this.currentFilters);
		}

		if (criterionFilterNode !== undefined) {
			this.mergedFilter = criterionFilterNode;
			this.filters = luceneToString(criterionFilterNode);
		}
		this.user = this.userService.user;
		this.requiredFields.push(
			'qtyBatchGross',
			'steelgradeDescription',
			'customerArticleReference'
		);
	}

	get dataId() {
		return `order-tracking-batch-${
			this.sortWay
		}-${
			this.sortBy
		}-${
			this.filters
		}`;
	}

	ngOnInit(): void {
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.filters = value !== ''
				? this.mergedFilter
					? this.createQuickSearchAndFilterQuery(this.createQuickSearchFilter(value), this.mergedFilter)
					: this.createQuickSearchFilter(value)
				: this.mergedFilter ? luceneToString(this.mergedFilter) : '';
		});
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<Batch[]>) => void) {
		provideData(
			this.batchService.getBatches(
				this.requiredFields,
				this.filters,
				index,
				limit,
				new OrderBy(this.sortBy, this.sortWay),
				false
			)
				.pipe(map(res => res.results))
		);
	}

	getBatchQuantity(batch: Batch) {
		return batch.qtyBatchGross;
	}

	createQuickSearchAndFilterQuery(quicksearchQuery: string, filterQuery: LuceneExpression) {
		const globalNode: NodeExpression = {
			left: luceneToParse(quicksearchQuery),
			operator: 'AND',
			right: filterQuery,
			parenthesized: false
		};

		return luceneToString(globalNode);
	}

	createQuickSearchFilter(textSearch: string) {
		const escapedTextFilter = `*${ term.escape(textSearch) }*`;

		const supplierOrderReferenceField: FieldExpression = {
			field: 'supplierOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const customerOrderReferenceField: FieldExpression = {
			field: 'customerOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const customerArticleReferenceField: FieldExpression = {
			field: 'customerArticleReference',
			term: escapedTextFilter,
			quoted: false
		};

		const steelgradeDescriptionField: FieldExpression = {
			field: 'steelgradeDescription',
			term: escapedTextFilter,
			quoted: false
		};

		const batchReferenceDescriptionField: FieldExpression = {
			field: 'batchReference',
			term: escapedTextFilter,
			quoted: false
		};

		const supplierCustomerNode = {
			left: supplierOrderReferenceField,
			operator: 'OR',
			right: customerOrderReferenceField,
			parenthesized: false
		};

		const articleSteelgradeNode = {
			left: customerArticleReferenceField,
			operator: 'OR',
			right: steelgradeDescriptionField,
			parenthesized: false
		};

		const intermediateNode = {
			left: supplierCustomerNode,
			operator: 'OR',
			right: articleSteelgradeNode,
			parenthesized: false
		};

		const globalNode = {
			left: intermediateNode,
			operator: 'OR',
			right: batchReferenceDescriptionField,
			parenthesized: true
		};

		return luceneToString(globalNode);
	}

	async goToFilter() {
		if (!this.currentFilters) {
			return;
		}
		this.listFilterService.filterToEdit = {
			filterState: this.currentFilters,
			datacenterType: this.currentFilters.datacenterType
		};
		await this.router.navigate(
			['filter'],
			{ queryParams: { type: this.datacenterType }, relativeTo: this.route }
		);
	}

	private getAppliedFilters() {
		const listFilter: ListFilter = {
			filterState: [],
			datacenterType: this.datacenterType
		};
		const appliedFilter = this.listFilterService.getAppliedFilters(this.datacenterType)
			?? listFilter;
		this.listFilterService.setCurrentFilter(appliedFilter);
		return appliedFilter;
	}
}
